import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  Title,
} from '@mantine/core'
import { IconChartBar, IconCircleCheck, IconClock, IconRefresh, IconUserCheck, IconUsers } from '@/components/icons'
import {
  OperatorTicketsModal,
  type OperatorTicketsBlock,
} from '@/components/dashboard/OperatorTicketsModal'
import { ScheduleShiftsTodayCard } from '@/components/schedules/ScheduleShiftsTodayCard'
import { PageShell } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import {
  buildDashboardPeriodParams,
  getPreviousPeriodParams,
  type DashboardPeriodKey,
} from '@/lib/dashboard-period'
import { isAIOperator } from '@/lib/dashboard-ai-operator'
import type {
  ColleaguesTodayResponse,
  MeDoneByDayItem,
  OperatorDetailedRow,
  ProjectRow,
} from '@/types/api'
import { getUserTimeZone } from '@/lib/user-timezone'

function toAnalyticsQuery(p: ReturnType<typeof buildDashboardPeriodParams>): Record<string, string | number | boolean> {
  const o: Record<string, string | number | boolean> = {}
  if (p.hours != null) o.hours = p.hours
  if (p.days != null) o.days = p.days
  if (p.date_time_from) o.date_time_from = p.date_time_from
  if (p.date_time_to) o.date_time_to = p.date_time_to
  if (p.project_id) o.project_id = p.project_id
  if (p.exclude_burst) o.exclude_burst = true
  o.tz = getUserTimeZone()
  return o
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function toLocalDatetimeValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

const KTU_HINT =
  'КТУ = (Все решенные + закрытые обращения) / Рабочие часы из расписания. Показывает общую производительность оператора. Без учета массовых закрытий и обращений без ответа.'

type DashboardTone = 'brand' | 'green' | 'blue' | 'orange' | 'grape' | 'gray'

function dashboardSurfaceStyle(tone: DashboardTone = 'gray') {
  const tint =
    tone === 'brand'
      ? 'var(--mantine-color-brand-light)'
      : tone === 'green'
        ? 'var(--mantine-color-green-light)'
        : tone === 'blue'
          ? 'var(--mantine-color-blue-light)'
          : tone === 'orange'
            ? 'var(--mantine-color-orange-light)'
            : tone === 'grape'
              ? 'var(--mantine-color-grape-light)'
              : 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))'
  return {
    background: `linear-gradient(135deg, color-mix(in srgb, ${tint} 46%, var(--mantine-color-body)), light-dark(#ffffff, var(--mantine-color-dark-7)))`,
    borderColor: 'color-mix(in srgb, var(--mantine-color-default-border) 82%, transparent)',
    boxShadow: '0 1px 2px color-mix(in srgb, var(--mantine-color-black) 4%, transparent)',
  }
}

const PersonalDashboardCharts = lazy(async () =>
  import('@/components/dashboard/PersonalDashboardCharts').then((m) => ({ default: m.PersonalDashboardCharts })),
)

type ComparisonMetric = {
  label: string
  value: string
  average: string
  diff: string
  better?: boolean | null
  hint?: string
}

export function DashboardPage() {
  const { user } = useAuth()

  const [period, setPeriod] = useState<DashboardPeriodKey>('7')
  const [projectId, setProjectId] = useState('')
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [customFrom, setCustomFrom] = useState(() => {
    const t = new Date()
    const from = new Date(t.getTime() - 30 * 24 * 60 * 60 * 1000)
    return toLocalDatetimeValue(from)
  })
  const [customTo, setCustomTo] = useState(() => toLocalDatetimeValue(new Date()))
  const [customApplied, setCustomApplied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [chartsLoading, setChartsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [meQuick, setMeQuick] = useState<OperatorDetailedRow | null>(null)
  const [prevMeQuick, setPrevMeQuick] = useState<OperatorDetailedRow | null>(null)
  const [operators, setOperators] = useState<OperatorDetailedRow[]>([])
  const [prevOperators, setPrevOperators] = useState<OperatorDetailedRow[]>([])
  const [meDoneByDay, setMeDoneByDay] = useState<MeDoneByDayItem[]>([])
  const [colleaguesToday, setColleaguesToday] = useState<ColleaguesTodayResponse | null>(null)
  const [colleaguesErr, setColleaguesErr] = useState<string | null>(null)

  const [ticketsModal, setTicketsModal] = useState<{
    block: OperatorTicketsBlock
    label: string
  } | null>(null)

  useEffect(() => {
    void api.projects().then((pl) => setProjects([...pl].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))))
  }, [])

  const customPeriodPending = period === 'custom_datetime' && !customApplied
  const handlePeriodChange = useCallback((next: DashboardPeriodKey) => {
    setPeriod(next)
    setCustomApplied(next !== 'custom_datetime')
  }, [])
  const handleCustomApply = useCallback(() => setCustomApplied(true), [])

  const periodParams = useMemo(() => {
    let customFromIso: string | null = null
    let customToIso: string | null = null
    if (period === 'custom_datetime' && customApplied && customFrom && customTo) {
      const df = new Date(customFrom)
      const dt = new Date(customTo)
      if (!Number.isNaN(df.getTime())) customFromIso = df.toISOString()
      if (!Number.isNaN(dt.getTime())) customToIso = dt.toISOString()
    }
    return buildDashboardPeriodParams(period, {
      projectId: projectId || undefined,
      customFromIso,
      customToIso,
      excludeBurst: false,
    })
  }, [period, projectId, customFrom, customTo, customApplied])

  const analyticsQuery = useMemo(() => toAnalyticsQuery(periodParams), [periodParams])
  // Freeze the query that produced the visible cards — updated only when load accepts data.
  const [drilldownAnalyticsQuery, setDrilldownAnalyticsQuery] = useState(analyticsQuery)
  const loadGenRef = useRef(0)
  const prevQuery = useMemo(() => {
    const prev = getPreviousPeriodParams(periodParams)
    return prev ? toAnalyticsQuery(prev) : null
  }, [periodParams])

  const refetchColleaguesOnly = useCallback(async () => {
    try {
      const col = await api.scheduleColleaguesToday()
      setColleaguesToday(col)
      setColleaguesErr(null)
    } catch (e) {
      setColleaguesToday(null)
      setColleaguesErr(
        e instanceof ApiError ? e.message : 'Не удалось загрузить список коллег на смене',
      )
    }
  }, [])

  const load = useCallback(async () => {
    const gen = ++loadGenRef.current
    if (customPeriodPending) {
      setLoading(false)
      setChartsLoading(false)
      return
    }
    if (period === 'custom_datetime' && (!customFrom || !customTo)) {
      setError('Укажите даты своего периода и нажмите «Показать».')
      setLoading(false)
      setChartsLoading(false)
      return
    }
    const acceptedQuery = analyticsQuery
    setLoading(true)
    setChartsLoading(true)
    setError(null)
    try {
      const mePromise = api.operatorsDetailedMe(analyticsQuery)
      const opPromise = api.operatorsDetailed(analyticsQuery)
      const prevPromise = prevQuery ? api.operatorsDetailed(prevQuery) : Promise.resolve({ operators: [] as OperatorDetailedRow[] })
      const prevMePromise = prevQuery ? api.operatorsDetailedMe(prevQuery) : Promise.resolve({ operator: null as OperatorDetailedRow | null })
      const dayQuery: Record<string, string | number | boolean> = { days: 7 }
      if (periodParams.project_id) dayQuery.project_id = periodParams.project_id
      const dayPromise = api.meDoneByDay(dayQuery)
      const colPromise = api.scheduleColleaguesToday()
      // Attach handlers before awaiting me so a stale cancel cannot leave rejections unhandled.
      const secondary = Promise.allSettled([opPromise, prevPromise, prevMePromise, dayPromise, colPromise])

      const meNow = await mePromise
      if (gen !== loadGenRef.current) {
        void secondary
        return
      }
      setMeQuick(meNow.operator ?? null)
      setDrilldownAnalyticsQuery(acceptedQuery)
      setLoading(false)

      const [opRes, prevRes, prevMeRes, dayRes, colRes] = await secondary
      if (gen !== loadGenRef.current) return

      if (opRes.status === 'fulfilled') {
        setOperators(opRes.value.operators ?? [])
      } else {
        setOperators([])
      }

      if (prevRes.status === 'fulfilled') {
        setPrevOperators(prevRes.value.operators ?? [])
      } else {
        setPrevOperators([])
      }
      if (prevMeRes.status === 'fulfilled') {
        setPrevMeQuick(prevMeRes.value.operator ?? null)
      } else {
        setPrevMeQuick(null)
      }

      if (dayRes.status === 'fulfilled') {
        setMeDoneByDay(dayRes.value.days ?? [])
      } else {
        setMeDoneByDay([])
      }

      if (colRes.status === 'fulfilled' && colRes.value) {
        setColleaguesToday(colRes.value)
        setColleaguesErr(null)
      } else if (colRes.status === 'rejected') {
        setColleaguesToday(null)
        setColleaguesErr(
          colRes.reason instanceof ApiError
            ? colRes.reason.message
            : 'Не удалось загрузить список коллег на смене',
        )
      }
      setChartsLoading(false)
    } catch (e) {
      if (gen !== loadGenRef.current) return
      setMeQuick(null)
      setPrevMeQuick(null)
      setOperators([])
      setPrevOperators([])
      setMeDoneByDay([])
      setColleaguesToday(null)
      setColleaguesErr(null)
      setChartsLoading(false)
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить дашборд')
    } finally {
      if (gen === loadGenRef.current) setLoading(false)
    }
  }, [period, customFrom, customTo, customPeriodPending, analyticsQuery, prevQuery, periodParams.project_id])

  useEffect(() => {
    void load()
  }, [load])

  const me = useMemo(() => {
    if (meQuick) return meQuick
    if (!user?.id) return null
    return operators.find((o) => o.operator_id === user.id) ?? null
  }, [meQuick, operators, user?.id])

  const prevMe = useMemo(() => {
    if (prevMeQuick) return prevMeQuick
    if (!user?.id) return null
    return prevOperators.find((o) => o.operator_id === user.id) ?? null
  }, [prevMeQuick, prevOperators, user?.id])

  const comparableOperators = useMemo(() => operators.filter(isComparableOperator), [operators])

  if (!user?.id) {
    return (
      <PageShell>
        <Alert color="red" title="Сессия">
          Не удалось определить пользователя. Войдите снова.
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="lg">
        <Card withBorder padding="md" radius="md" style={dashboardSurfaceStyle('brand')}>
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={2}>
              <Group gap="xs" align="center">
                <ThemeIcon variant="light" radius="md" size={34}>
                  <IconLayoutDashboardSafe />
                </ThemeIcon>
                <Title order={3}>Дашборд оператора</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Личные показатели, сравнение со средним и текущая нагрузка по сменам.
              </Text>
            </Stack>
            <DashboardFilters
              period={period}
              projectId={projectId}
              projects={projects}
              customFrom={customFrom}
              customTo={customTo}
              customApplied={customApplied}
              loading={loading}
              onPeriodChange={handlePeriodChange}
              onProjectChange={(next) => {
                if (customPeriodPending) return
                setProjectId(next)
              }}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              onCustomApply={handleCustomApply}
              onRefresh={() => void load()}
            />
          </Group>
        </Card>

        {customPeriodPending ? (
          <Alert color="blue" title="Свой период">
            Укажите даты и нажмите «Показать», чтобы загрузить данные.
          </Alert>
        ) : null}

        {error ? (
          <Alert color="red" title="Ошибка">
            {error}
          </Alert>
        ) : null}

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" verticalSpacing="md">
          <Card withBorder padding="sm" radius="md" style={dashboardSurfaceStyle('green')}>
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Stack gap={0}>
                  <Text size="sm" fw={700}>
                    Мои показатели
                  </Text>
                  <Text size="xs" c="dimmed">
                    Ключевые метрики за выбранный период
                  </Text>
                </Stack>
                {me ? <OperatorIdentity op={me} /> : null}
              </Group>
              {loading ? (
                <DashboardSkeleton height={76} />
              ) : !me ? (
                <CompactEmptyState text="Нет данных по вашей строке за выбранный период." />
              ) : (
                <PersonalOverview
                  op={me}
                  prevOp={prevMe}
                  onShowTickets={(block, label) => {
                    if (customPeriodPending) return
                    setTicketsModal({ block, label })
                  }}
                />
              )}
            </Stack>
          </Card>

          <Box style={{ minWidth: 0 }}>
            <ScheduleShiftsTodayCard
              showColleaguesList
              compact
              prominentActions
              dashboardLayout
              maxColleaguesHeight={116}
              colleaguesLoading={loading || chartsLoading}
              colleaguesErr={colleaguesErr}
              colleaguesToday={colleaguesToday}
              onShiftMutated={() => void refetchColleaguesOnly()}
            />
          </Box>
        </SimpleGrid>

        <Card withBorder padding="md" radius="md" style={dashboardSurfaceStyle('blue')}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Stack gap={0}>
                <Text size="sm" fw={700}>
                  Личные метрики против среднего
                </Text>
                <Text size="xs" c="dimmed">
                  Сравнение с операторами, у которых есть данные в выбранном периоде
                </Text>
              </Stack>
              <Badge variant="light" color="gray">
                Операторов: {comparableOperators.length}
              </Badge>
            </Group>
            {loading || chartsLoading ? (
              <DashboardSkeleton cols={6} />
            ) : me ? (
              <PersonalComparison me={me} operators={comparableOperators} />
            ) : (
              <Text c="dimmed" size="sm">
                Нет данных для сравнения.
              </Text>
            )}
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md" style={dashboardSurfaceStyle('brand')}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Stack gap={0}>
                <Text size="sm" fw={700}>
                  Графики
                </Text>
                <Text size="xs" c="dimmed">
                  Визуально как в «Аналитика New»: распределение, сравнение и динамика
                </Text>
              </Stack>
              {chartsLoading ? (
                <Badge variant="light" color="blue">
                  Догружаем
                </Badge>
              ) : null}
            </Group>
            <div style={{ minHeight: 280 }}>
              {loading || chartsLoading ? (
                <DashboardSkeleton cols={3} height={240} />
              ) : (
                <Suspense
                  fallback={
                    <Text c="dimmed" size="sm">
                      Подготовка графиков…
                    </Text>
                  }
                >
                  <PersonalDashboardCharts
                    me={me}
                    allOps={operators}
                    prevMe={prevMe}
                    prevAll={prevOperators}
                    doneByDay={meDoneByDay}
                    projects={projects}
                    selectedProjectId={projectId || null}
                  />
                </Suspense>
              )}
            </div>
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md" style={dashboardSurfaceStyle('gray')}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Stack gap={0}>
                <Text size="sm" fw={700}>
                  Детализация
                </Text>
                <Text size="xs" c="dimmed">
                  Полный набор показателей, включая отзывы, чужие обращения и КТУ
                </Text>
              </Stack>
            </Group>
            {loading ? (
              <DashboardSkeleton />
            ) : me ? (
              <MyOperatorPeriodStats
                op={me}
                onShowTickets={(block, label) => {
                  if (customPeriodPending) return
                  setTicketsModal({ block, label })
                }}
              />
            ) : (
              <Text c="dimmed" size="sm">
                Нет данных по вашей строке за выбранный период.
              </Text>
            )}
          </Stack>
        </Card>

        <OperatorTicketsModal
          opened={ticketsModal != null && !!user?.id}
          onClose={() => setTicketsModal(null)}
          operatorId={user.id}
          block={ticketsModal?.block ?? 'total_tickets'}
          blockLabel={ticketsModal?.label ?? ''}
          analyticsParams={drilldownAnalyticsQuery}
        />
      </Stack>
    </PageShell>
  )
}

function IconLayoutDashboardSafe() {
  return <IconChartBar size={18} stroke={1.7} />
}

function DashboardFilters({
  period,
  projectId,
  projects,
  customFrom,
  customTo,
  customApplied,
  loading,
  onPeriodChange,
  onProjectChange,
  onCustomFromChange,
  onCustomToChange,
  onCustomApply,
  onRefresh,
}: {
  period: DashboardPeriodKey
  projectId: string
  projects: ProjectRow[]
  customFrom: string
  customTo: string
  customApplied: boolean
  loading: boolean
  onPeriodChange: (value: DashboardPeriodKey) => void
  onProjectChange: (value: string) => void
  onCustomFromChange: (value: string) => void
  onCustomToChange: (value: string) => void
  onCustomApply: () => void
  onRefresh: () => void
}) {
  const [draftFrom, setDraftFrom] = useState(customFrom)
  const [draftTo, setDraftTo] = useState(customTo)
  useEffect(() => setDraftFrom(customFrom), [customFrom])
  useEffect(() => setDraftTo(customTo), [customTo])
  const draftChanged = draftFrom !== customFrom || draftTo !== customTo
  const draftInvalid = !draftFrom || !draftTo || new Date(draftFrom) >= new Date(draftTo)
  const canApply = !draftInvalid && (!customApplied || draftChanged)
  const applyDraft = () => {
    if (!canApply) return
    onCustomFromChange(draftFrom)
    onCustomToChange(draftTo)
    onCustomApply()
  }

  return (
    <Stack gap="xs" align="flex-end">
      <Group align="center" wrap="wrap" justify="flex-end" gap="sm">
        <Select
          size="xs"
          aria-label="Период"
          data={[
            { value: '1h', label: 'За час' },
            { value: 'today', label: 'За сегодня' },
            { value: '24h', label: 'За сутки' },
            { value: '7', label: 'За неделю' },
            { value: '30', label: 'За 30 дней' },
            { value: '90', label: 'За 90 дней' },
            { value: 'custom_datetime', label: 'Свой период' },
          ]}
          value={period}
          onChange={(value) => onPeriodChange((value || 'today') as DashboardPeriodKey)}
          allowDeselect={false}
          checkIconPosition="right"
          styles={{ root: { width: 150 }, input: { minHeight: 30 } }}
        />
        <Select
          size="xs"
          aria-label="Проект"
          data={[
            { value: '', label: 'Все проекты' },
            ...projects.map((pr) => ({ value: pr.id, label: pr.name || pr.slug || pr.id })),
          ]}
          value={projectId}
          onChange={(value) => onProjectChange(value || '')}
          searchable
          allowDeselect={false}
          checkIconPosition="right"
          styles={{ root: { width: 220 }, input: { minHeight: 30 } }}
        />
        <Button
          size="xs"
          leftSection={<IconRefresh size={14} stroke={1.5} />}
          variant="light"
          onClick={onRefresh}
          loading={loading}
          disabled={period === 'custom_datetime' && !customApplied}
        >
          Обновить
        </Button>
      </Group>
      {period === 'custom_datetime' ? (
        <Group align="center" wrap="wrap" gap="sm" justify="flex-end">
          <TextInput
            type="datetime-local"
            aria-label="От (дата и время)"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyDraft()
            }}
            size="xs"
            leftSection={<Text size="xs">От</Text>}
            styles={{ input: { paddingLeft: 34 } }}
          />
          <TextInput
            type="datetime-local"
            aria-label="До (дата и время)"
            value={draftTo}
            onChange={(e) => setDraftTo(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyDraft()
            }}
            size="xs"
            leftSection={<Text size="xs">До</Text>}
            styles={{ input: { paddingLeft: 34 } }}
          />
          <Button size="xs" onClick={applyDraft} disabled={!canApply}>
            Показать
          </Button>
        </Group>
      ) : null}
    </Stack>
  )
}

function DashboardSkeleton({ cols = 4, height = 92 }: { cols?: number; height?: number }) {
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: Math.min(cols, 4), lg: cols }} spacing="sm">
      {Array.from({ length: cols }).map((_, idx) => (
        <Skeleton key={idx} height={height} radius="md" />
      ))}
    </SimpleGrid>
  )
}

function CompactEmptyState({ text }: { text: string }) {
  return (
    <div
      style={{
        minHeight: 76,
        display: 'flex',
        alignItems: 'center',
        border: '1px dashed var(--mantine-color-default-border)',
        borderRadius: 8,
        padding: '10px 12px',
      }}
    >
      <Text c="dimmed" size="sm">
        {text}
      </Text>
    </div>
  )
}

function OperatorIdentity({ op }: { op: OperatorDetailedRow }) {
  const src =
    op.avatar_url ||
    null
  return (
    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
      <Avatar src={src || undefined} size={32} radius="xl">
        {(op.operator_name || op.operator_email || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} truncate="end">
          {op.operator_name || op.operator_email || 'Оператор'}
        </Text>
        {op.operator_email ? (
          <Text size="xs" c="dimmed" truncate="end">
            {op.operator_email}
          </Text>
        ) : null}
      </Stack>
    </Group>
  )
}

function PersonalOverview({
  op,
  prevOp,
  onShowTickets,
}: {
  op: OperatorDetailedRow
  prevOp: OperatorDetailedRow | null
  onShowTickets: (block: OperatorTicketsBlock, label: string) => void
}) {
  const ktuMain = op.ktu_with_burst != null ? op.ktu_with_burst : op.ktu
  return (
    <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
      <HeroMetric
        icon={<IconUsers size={18} stroke={1.7} />}
        label="Обращения"
        value={formatPlainNumber(op.total_tickets)}
        sub={diffLine(op.total_tickets, prevOp?.total_tickets)}
        color="blue"
        clickable={(op.total_tickets || 0) > 0}
        onClick={() => onShowTickets('total_tickets', 'Обращений за период')}
      />
      <HeroMetric
        icon={<IconCircleCheck size={18} stroke={1.7} />}
        label="Решено и закрыто"
        value={formatPlainNumber((op.resolved_tickets || 0) + (op.closed_tickets || 0))}
        sub={diffLine(
          (op.resolved_tickets || 0) + (op.closed_tickets || 0),
          (prevOp?.resolved_tickets || 0) + (prevOp?.closed_tickets || 0),
        )}
        color="green"
      />
      <HeroMetric
        icon={<IconClock size={18} stroke={1.7} />}
        label="Средний ответ"
        value={formatMinutesValue(op.avg_reply_minutes)}
        sub={diffLine(op.avg_reply_minutes, prevOp?.avg_reply_minutes, true)}
        color="orange"
      />
      <HeroMetric
        icon={<IconUserCheck size={18} stroke={1.7} />}
        label="КТУ"
        value={ktuMain != null ? ktuMain.toFixed(2) : '—'}
        sub={diffLine(ktuMain, prevOp?.ktu_with_burst ?? prevOp?.ktu)}
        color="grape"
      />
    </SimpleGrid>
  )
}

function HeroMetric({
  icon,
  label,
  value,
  sub,
  color,
  clickable,
  onClick,
}: {
  icon: ReactNode
  label: string
  value: string
  sub: string
  color: string
  clickable?: boolean
  onClick?: () => void
}) {
  const accent = `var(--mantine-color-${color}-6)`
  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      role={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      style={{
        cursor: clickable ? 'pointer' : 'default',
        background: `linear-gradient(135deg, color-mix(in srgb, var(--mantine-color-${color}-light) 54%, var(--mantine-color-body)), light-dark(#ffffff, var(--mantine-color-dark-7)))`,
        borderColor: 'color-mix(in srgb, var(--mantine-color-default-border) 78%, transparent)',
        minHeight: 96,
        boxShadow: '0 1px 2px color-mix(in srgb, var(--mantine-color-black) 4%, transparent)',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="stretch" wrap="nowrap" style={{ minWidth: 0 }}>
          <span
            aria-hidden
            style={{
              width: 5,
              borderRadius: 999,
              background: accent,
              flexShrink: 0,
            }}
          />
          <Stack gap={4} style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed" fw={600}>
            {label}
          </Text>
          <Text fz={24} fw={750} lh={1.05}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {sub}
          </Text>
          </Stack>
        </Group>
        <ThemeIcon variant="light" color={color} radius="md" size={36}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  )
}

function PersonalComparison({ me, operators }: { me: OperatorDetailedRow; operators: OperatorDetailedRow[] }) {
  const metrics = buildComparisonMetrics(me, operators)
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="sm">
      {metrics.map((metric) => (
        <ComparisonCard key={metric.label} metric={metric} />
      ))}
    </SimpleGrid>
  )
}

function ComparisonCard({ metric }: { metric: ComparisonMetric }) {
  const color = metric.better == null ? 'gray' : metric.better ? 'green' : 'orange'
  const tone: DashboardTone = metric.better == null ? 'gray' : metric.better ? 'green' : 'orange'
  return (
    <Card withBorder padding="sm" radius="sm" style={{ ...dashboardSurfaceStyle(tone), minHeight: 142 }}>
      <Stack gap={7}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="xs" c="dimmed" fw={600} lineClamp={2}>
            {metric.label}
          </Text>
          <Tooltip
            label={metric.hint || 'Сравнение вашего значения со средним по операторам за выбранный период'}
            withArrow
            multiline
            w={280}
          >
            <Badge size="xs" variant="light" color={color} style={{ cursor: 'help' }}>
              {metric.diff}
            </Badge>
          </Tooltip>
        </Group>
        <Text fw={700} fz={22} lh={1}>
          {metric.value}
        </Text>
        <Divider />
        <Text size="xs" c="dimmed">
          Среднее: {metric.average}
        </Text>
        {metric.hint ? (
          <Text size="xs" c="dimmed" lh={1.35}>
            {metric.hint}
          </Text>
        ) : null}
      </Stack>
    </Card>
  )
}

function finiteNumber(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

function averageOf(rows: OperatorDetailedRow[], pick: (row: OperatorDetailedRow) => number | null | undefined): number | null {
  const values = rows.map((row) => finiteNumber(pick(row))).filter((value): value is number => value != null)
  if (!values.length) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

function buildComparisonMetrics(me: OperatorDetailedRow, operators: OperatorDetailedRow[]): ComparisonMetric[] {
  const humanOps = operators.filter(isComparableOperator)
  const finished = (row: OperatorDetailedRow) => (row.resolved_tickets || 0) + (row.closed_tickets || 0)
  const reviewRatio = (row: OperatorDetailedRow) => {
    if (row.ratio_closed_with_review != null) return row.ratio_closed_with_review * 100
    const closed = row.closed_tickets || 0
    if (!closed) return null
    return ((row.closed_with_review || 0) / closed) * 100
  }
  const ktu = (row: OperatorDetailedRow) => row.ktu_with_burst ?? row.ktu

  return [
    comparisonMetric({
      label: 'Обработано обращений',
      mine: me.total_tickets,
      avg: averageOf(humanOps, (row) => row.total_tickets),
      formatter: formatPlainNumber,
      higherIsBetter: true,
      hint: 'Общая нагрузка оператора относительно команды.',
    }),
    comparisonMetric({
      label: 'Решено + закрыто',
      mine: finished(me),
      avg: averageOf(humanOps, finished),
      formatter: formatPlainNumber,
      higherIsBetter: true,
      hint: 'Фактический результат за период.',
    }),
    comparisonMetric({
      label: 'Средний ответ клиенту',
      mine: me.avg_reply_minutes,
      avg: averageOf(humanOps, (row) => row.avg_reply_minutes),
      formatter: formatMinutesValue,
      higherIsBetter: false,
      hint: 'Ниже среднего лучше: клиент быстрее получает ответ.',
    }),
    comparisonMetric({
      label: 'Первый ответ',
      mine: me.avg_response_time_minutes,
      avg: averageOf(humanOps, (row) => row.avg_response_time_minutes),
      formatter: formatMinutesValue,
      higherIsBetter: false,
      hint: 'Скорость реакции после назначения.',
    }),
    comparisonMetric({
      label: 'КТУ',
      mine: ktu(me),
      avg: averageOf(humanOps, ktu),
      formatter: (value) => (value == null ? '—' : value.toFixed(2)),
      higherIsBetter: true,
      hint: 'Производительность с учетом рабочих часов.',
    }),
    comparisonMetric({
      label: 'Закрыто с оценкой',
      mine: reviewRatio(me),
      avg: averageOf(humanOps, reviewRatio),
      formatter: formatPercentValue,
      higherIsBetter: true,
      hint: 'Доля закрытых обращений, где клиент оставил оценку.',
    }),
  ]
}

const COMPARABLE_OPERATOR_ROLES = new Set([
  'operator',
  'trainee',
  'shift_lead',
  'npc_helper',
  'human_helper',
])

function isComparableOperator(op: OperatorDetailedRow): boolean {
  if (isAIOperator(op)) return false
  const role = String(op.operator_role || '').trim().toLowerCase()
  if (role && !COMPARABLE_OPERATOR_ROLES.has(role)) return false
  return operatorHasPeriodActivity(op)
}

function operatorHasPeriodActivity(op: OperatorDetailedRow): boolean {
  const finished = (op.resolved_tickets || 0) + (op.closed_tickets || 0)
  return (
    (op.total_tickets || 0) > 0 ||
    (op.opened_tickets || 0) > 0 ||
    (op.in_work_tickets || 0) > 0 ||
    finished > 0 ||
    (op.replied_tickets || 0) > 0 ||
    (op.reply_messages || 0) > 0 ||
    (op.foreign_replied_tickets || 0) > 0 ||
    (op.foreign_resolved_tickets || 0) > 0 ||
    (op.foreign_closed_after_resolved_tickets || 0) > 0 ||
    (op.product_review_requests_sent || 0) > 0 ||
    (op.product_review_requests_received || 0) > 0
  )
}

function comparisonMetric({
  label,
  mine,
  avg,
  formatter,
  higherIsBetter,
  hint,
}: {
  label: string
  mine: number | null | undefined
  avg: number | null
  formatter: (value: number | null | undefined) => string
  higherIsBetter: boolean
  hint?: string
}): ComparisonMetric {
  const mineValue = finiteNumber(mine)
  const avgValue = finiteNumber(avg)
  if (mineValue == null || avgValue == null) {
    return {
      label,
      value: formatter(mineValue),
      average: formatter(avgValue),
      diff: 'нет данных',
      better: null,
      hint,
    }
  }
  const delta = mineValue - avgValue
  const abs = Math.abs(delta)
  const isBetter = abs < 0.01 ? null : higherIsBetter ? delta > 0 : delta < 0
  return {
    label,
    value: formatter(mineValue),
    average: formatter(avgValue),
    diff: abs < 0.01 ? 'на уровне' : `${delta > 0 ? '+' : '-'}${formatter(abs)}`,
    better: isBetter,
    hint,
  }
}

function formatPlainNumber(value: number | null | undefined): string {
  const n = finiteNumber(value)
  if (n == null) return '—'
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

function formatMinutesValue(value: number | null | undefined): string {
  const n = finiteNumber(value)
  if (n == null) return '—'
  return `${Math.round(n)} мин`
}

function formatPercentValue(value: number | null | undefined): string {
  const n = finiteNumber(value)
  if (n == null) return '—'
  return `${Math.round(n)}%`
}

function diffLine(current: number | null | undefined, previous: number | null | undefined, lowerIsBetter = false): string {
  const cur = finiteNumber(current)
  const prev = finiteNumber(previous)
  if (cur == null || prev == null) return 'Предыдущий период: нет данных'
  const delta = cur - prev
  if (Math.abs(delta) < 0.01) return 'Без изменений к прошлому периоду'
  const good = lowerIsBetter ? delta < 0 : delta > 0
  const sign = delta > 0 ? '+' : '-'
  return `${good ? 'Лучше' : 'Хуже'} прошлого: ${sign}${formatPlainNumber(Math.abs(delta))}`
}

function MyOperatorPeriodStats({
  op,
  onShowTickets,
}: {
  op: OperatorDetailedRow
  onShowTickets: (block: OperatorTicketsBlock, label: string) => void
}) {
  const fmt = (v: number | null | undefined, suf = '') =>
    v != null ? (typeof v === 'number' && v % 1 !== 0 ? v.toFixed(1) : v) + suf : '—'
  const ktuMain = op.ktu_with_burst != null ? op.ktu_with_burst : op.ktu

  return (
    <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="sm">
      <StatCell
        label="Обращений за период"
        value={fmt(op.total_tickets)}
        showList={(op.total_tickets || 0) > 0}
        onList={() => onShowTickets('total_tickets', 'Обращений за период')}
      />
      <StatCell label="Открыто" value={fmt(op.opened_tickets)} />
      <StatCell label="В работе" value={fmt(op.in_work_tickets)} />
      <StatCell
        label="Решено"
        value={fmt(op.resolved_tickets)}
        valueColor="var(--mantine-color-green-filled)"
        showList={(op.resolved_tickets || 0) > 0}
        onList={() => onShowTickets('resolved_tickets', 'Решено')}
      />
      <StatCell
        label="Закрыто за период"
        value={fmt(op.closed_tickets)}
        showList={(op.closed_tickets || 0) > 0}
        onList={() => onShowTickets('closed_tickets', 'Закрыто за период')}
      />
      <StatCell
        label="Закрыто с оценкой"
        value={fmt(op.closed_with_review)}
        showList={(op.closed_with_review || 0) > 0}
        onList={() => onShowTickets('closed_with_review', 'Закрыто с оценкой')}
      />
      <StatCell
        label="Закрыто без оценки"
        value={fmt(op.closed_without_review)}
        showList={(op.closed_without_review || 0) > 0}
        onList={() => onShowTickets('closed_without_review', 'Закрыто без оценки')}
      />
      <StatCell
        label="Соотношение с оценкой"
        value={
          op.ratio_closed_with_review != null
            ? `${(op.ratio_closed_with_review * 100).toFixed(0)}%`
            : '—'
        }
      />
      <StatCell label="Рабочих часов" value={fmt(op.working_hours, ' ч')} />
      <StatCell label="Чужие: ответил" value={fmt(op.foreign_replied_tickets)} />
      <StatCell label="Чужие: решено" value={fmt(op.foreign_resolved_tickets)} />
      <StatCell label="Чужие: закрыто" value={fmt(op.foreign_closed_after_resolved_tickets)} />
      <StatCell
        label={
          <Group gap={6} wrap="nowrap" align="center">
            <span>КТУ (решено+закрыто/час)</span>
            <Tooltip label={KTU_HINT} multiline w={360} withArrow>
              <Text component="span" size="xs" c="dimmed" style={{ cursor: 'help' }}>
                ⓘ
              </Text>
            </Tooltip>
          </Group>
        }
        value={ktuMain != null ? ktuMain.toFixed(2) : '—'}
      />
      <StatCell
        label="Оценок / ср. оценка"
        value={`${fmt(op.reviews_count)} / ${op.avg_rating != null ? op.avg_rating.toFixed(1) : '—'}`}
      />
      <StatCell
        label="Запросов отзывов о продукте"
        value={`${op.product_review_requests_sent ?? 0} / ${op.product_review_requests_received ?? 0}`}
        showList={(op.product_review_requests_sent ?? 0) > 0}
        onList={() => onShowTickets('product_review_requests', 'Запросы отзывов о продукте')}
      />
      <StatCell
        label="Ср. время первого ответа"
        value={
          op.avg_response_time_minutes != null
            ? `${Math.round(op.avg_response_time_minutes)} мин`
            : '—'
        }
      />
      <StatCell
        label="Ср. время ответа"
        value={
          op.avg_reply_minutes != null ? `${Math.round(op.avg_reply_minutes)} мин` : '—'
        }
      />
      <StatCell
        label="Ср. время решения"
        value={
          op.avg_resolution_time_minutes != null
            ? `${Math.round(op.avg_resolution_time_minutes)} мин`
            : '—'
        }
      />
    </SimpleGrid>
  )
}

function StatCell({
  label,
  value,
  valueColor,
  showList,
  onList,
}: {
  label: ReactNode
  value: string
  valueColor?: string
  showList?: boolean
  onList?: () => void
}) {
  const numericValue = Number(String(value).replace(/[^\d.-]/g, ''))
  const hasValue = Number.isFinite(numericValue) && numericValue > 0
  return (
    <Card
      withBorder
      padding="sm"
      radius="sm"
      style={{
        ...dashboardSurfaceStyle(hasValue ? 'blue' : 'gray'),
        minHeight: 112,
      }}
    >
      <Stack gap={4} h="100%">
        <Text size="xs" c="dimmed" fw={600} lineClamp={2} style={{ minHeight: 32 }}>
          {label}
        </Text>
        <Text fw={700} fz={20} lh={1.1} style={valueColor ? { color: valueColor } : undefined}>
          {value}
        </Text>
      </Stack>
      {showList && onList ? (
        <Button size="compact-xs" variant="light" mt={6} onClick={onList}>
          Показать список
        </Button>
      ) : null}
    </Card>
  )
}
